import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useOnboarding, TOUR_STEPS } from '../contexts/OnboardingContext';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const TOOLTIP_WIDTH = 320;

const GuidedTour: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { tourActive, tourStep, nextStep, prevStep, skipTour } = useOnboarding();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const pollRef = useRef<number | null>(null);
  const autoAdvancedRef = useRef<Set<number>>(new Set());

  const currentStep = TOUR_STEPS[tourStep];

  // Poll for target element (handles cross-page navigation)
  const findTarget = useCallback(() => {
    if (!currentStep) return;

    if (pollRef.current) {
      cancelAnimationFrame(pollRef.current);
      pollRef.current = null;
    }

    const startTime = Date.now();
    const poll = () => {
      const el = document.querySelector(currentStep.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
        return;
      }

      // Timeout after 2 seconds — auto-advance to next step if element not found
      if (Date.now() - startTime > 2000) {
        setTargetRect(null);
        // Auto-skip this step if we haven't already (prevents infinite loops)
        if (!autoAdvancedRef.current.has(tourStep)) {
          autoAdvancedRef.current.add(tourStep);
          nextStep();
        }
        return;
      }

      pollRef.current = requestAnimationFrame(poll);
    };

    // Small initial delay for DOM settling after navigation
    setTimeout(poll, 100);
  }, [currentStep, tourStep, nextStep]);

  useEffect(() => {
    if (!tourActive) {
      setTargetRect(null);
      autoAdvancedRef.current.clear();
      return;
    }

    findTarget();

    // Re-find on scroll/resize
    const handleReposition = () => {
      if (!currentStep) return;
      const el = document.querySelector(currentStep.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      if (pollRef.current) cancelAnimationFrame(pollRef.current);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [tourActive, tourStep, findTarget, currentStep]);

  if (!tourActive || !currentStep) return null;

  // Spotlight cutout clip-path
  const clipPath = targetRect
    ? `polygon(
        0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
        ${targetRect.left - PADDING}px ${targetRect.top - PADDING}px,
        ${targetRect.left + targetRect.width + PADDING}px ${targetRect.top - PADDING}px,
        ${targetRect.left + targetRect.width + PADDING}px ${targetRect.top + targetRect.height + PADDING}px,
        ${targetRect.left - PADDING}px ${targetRect.top + targetRect.height + PADDING}px,
        ${targetRect.left - PADDING}px ${targetRect.top - PADDING}px
      )`
    : undefined;

  // Tooltip positioning: prefer below target, else above. On mobile: bottom sheet.
  let tooltipStyle: React.CSSProperties = {};
  if (isMobile) {
    tooltipStyle = {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%',
      borderRadius: '16px 16px 0 0',
    };
  } else if (targetRect) {
    const spaceBelow = window.innerHeight - (targetRect.top + targetRect.height + PADDING);
    const spaceRight = window.innerWidth - (targetRect.left + targetRect.width + PADDING);

    if (spaceBelow > 200) {
      // Below target
      tooltipStyle = {
        position: 'fixed',
        top: targetRect.top + targetRect.height + PADDING + 12,
        left: Math.min(
          Math.max(targetRect.left, 16),
          window.innerWidth - TOOLTIP_WIDTH - 16,
        ),
      };
    } else if (targetRect.top > 200) {
      // Above target
      tooltipStyle = {
        position: 'fixed',
        bottom: window.innerHeight - targetRect.top + PADDING + 12,
        left: Math.min(
          Math.max(targetRect.left, 16),
          window.innerWidth - TOOLTIP_WIDTH - 16,
        ),
      };
    } else if (spaceRight > TOOLTIP_WIDTH + 24) {
      // Right of target
      tooltipStyle = {
        position: 'fixed',
        top: Math.max(targetRect.top, 16),
        left: targetRect.left + targetRect.width + PADDING + 12,
      };
    } else {
      // Left of target
      tooltipStyle = {
        position: 'fixed',
        top: Math.max(targetRect.top, 16),
        right: window.innerWidth - targetRect.left + PADDING + 12,
      };
    }
  } else {
    // No target found — center the tooltip
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  return (
    <>
      {/* Semi-transparent overlay with spotlight cutout */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(74, 55, 40, 0.5)',
          zIndex: 1300,
          clipPath: clipPath,
          transition: 'clip-path 0.3s ease',
        }}
      />

      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          ...tooltipStyle,
          zIndex: 1301,
          width: isMobile ? '100%' : TOOLTIP_WIDTH,
          maxWidth: '100vw',
          border: '1px solid #F0E4CE',
          p: 2.5,
        }}
      >
        {/* Step indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          {TOUR_STEPS.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: idx === tourStep ? '#3A8C8C' : '#F0E4CE',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
          <Typography
            variant="caption"
            sx={{ ml: 'auto', color: '#7A5F4E', fontSize: 11 }}
          >
            {tourStep + 1} of {TOUR_STEPS.length}
          </Typography>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: '#4A3728', mb: 0.5, fontFamily: '"Fraunces", serif' }}
        >
          {currentStep.title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#7A5F4E', lineHeight: 1.6, mb: 2 }}>
          {currentStep.description}
        </Typography>

        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            size="small"
            onClick={skipTour}
            sx={{ color: '#7A5F4E', textTransform: 'none', fontSize: 12 }}
          >
            Skip tour
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {tourStep > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={prevStep}
                sx={{
                  textTransform: 'none',
                  borderColor: '#F0E4CE',
                  color: '#4A3728',
                  '&:hover': { borderColor: '#7A5F4E' },
                }}
              >
                Back
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={nextStep}
              sx={{
                textTransform: 'none',
                backgroundColor: '#3A8C8C',
                '&:hover': { backgroundColor: '#2A6666' },
              }}
            >
              {tourStep === TOUR_STEPS.length - 1 ? "Let's chat!" : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default GuidedTour;
